import { SPRINT, TIERS } from "@/lib/pricing";

/**
 * Cross-promotion for Audit Friendly's paid products and sister properties.
 *
 * Job-seeker surfaces (job rail, apply success, home grid, /pricing, the footer
 * on job-board pages) show job-seeker products only: the Still Remote pass
 * first, then the Search Sprint, Hired and Candidate Pro. The close tools
 * (Library, Playbook, close kits) belong on the Library and close landing
 * pages and in Month-End Closers, not on job pages (in the 7 days to
 * 2026-10-27 the Library footer offer drew 4,824 views and the Playbook 690,
 * with 0 clicks for either).
 *
 * One registry so every surface (home grid, job page rail, alert and apply
 * success screens, /data/remote-first, nav and footer) says the same thing at
 * the same price. <OfferSlot/> renders one of these and records offer_view and
 * offer_click in site_events with { key, surface }.
 *
 * Prices are read from code, never retyped:
 *   Candidate Lite / Pro   src/lib/pricing.ts (TIERS)
 *   Search Sprint          src/lib/pricing.ts (SPRINT: $29 once, 30 days)
 *   Still Remote pass      src/lib/pricing.ts (SPRINT.directoryPriceDisplay / directoryDays),
 *                          mirroring supabase/functions/_shared/directory-lib.ts PRICE_CENTS
 *                          ($24, 90 days, nothing renews)
 *   CSV lists              supabase/functions/_shared/list-products.ts ($19)
 *   Post a job             create-job-posting-checkout ($49 / promoted from partner_packages)
 *   Jobs API               src/pages/data/DataApi.tsx (from $149/mo)
 *   Library and Playbook   src/data/library/catalog.json bundles (LIBRARY_PRICES
 *                          below mirrors it; the catalog is 70 KB, too big to
 *                          pull into every page for six numbers)
 *
 * The recruiter CSV lists are NOT purchasable yet (purchasable: false in
 * list-products.ts: specialties are empty and one list has 23 of 50 rows), so
 * the recruiter_list offer is `enabled: false` and no slot will pick it.
 *
 * The job alert email carries its own copy of the email offers in
 * supabase/functions/send-job-alerts/index.ts (offerFor, and offerLineFor for
 * the small Library, Playbook and Sprint line); keep them agreed.
 */

export type OfferKey =
  | "still_remote"
  | "still_remote_friday"
  | "remote_accountant"
  | "finance_nomad"
  | "candidate_pro"
  | "search_sprint"
  | "hired"
  | "recruiter_list"
  | "remote_companies_list"
  | "jobs_api"
  | "post_a_job"
  // AuditFriendly Library (2026-10-19). Bundle keys are the catalog bundle
  // slug with dashes as underscores, so checkout_start can name the same id.
  | "library"
  | "close_prompt_playbook"
  | "close_playbook_skills"
  | "pro_month_end_close_kit"
  | "smb_year_end_kit"
  | "smb_cleanup_kit"
  | "smb_monthly_books_kit";

export type OfferSegment = "remote" | "accounting" | "finance" | "employer" | "smb" | "all";

export type OfferSurface =
  | "home_grid"
  | "job_rail"
  | "alert_success"
  | "apply_success"
  | "remote_first"
  | "nav"
  | "footer"
  /** The one-line Playbook note under the apply_success card (close roles). Retired 2026-10-27; kept for old rows. */
  | "apply_success_close"
  /** One-time products band on /pricing. */
  | "pricing"
  /** Primary CTA in the hero of /library, /library/pros and /small-business. */
  | "library_hero"
  | "pros_hero"
  | "smb_hero"
  /** The three job-to-be-done landing pages (LIB-01, 2026-10-20): hero CTA, price ladder or skill cards, closing CTA. */
  | "close_landing_hero"
  | "close_landing_ladder"
  | "close_landing_sample"
  | "close_landing_bottom"
  | "yearend_landing_hero"
  | "yearend_landing_free"
  | "yearend_landing_kit"
  | "yearend_landing_bottom"
  | "skills_landing_hero"
  | "skills_landing_skill"
  | "skills_landing_bottom"
  /** The "Start with a guide" links on the /library hubs that lead to those pages. */
  | "library_guides";

export interface Offer {
  key: OfferKey;
  /** Small label above the headline. */
  kicker: string;
  headline: string;
  body: string;
  cta: string;
  /** Base URL, before UTMs. Internal paths start with "/". */
  url: string;
  /** Display price, or null when free. */
  price: string | null;
  paid: boolean;
  segments: OfferSegment[];
  /** False while the product cannot actually be bought. */
  enabled: boolean;
  /** One short sentence for <OfferLine/>, the lighter text-link placement. */
  line?: string;
}

const PRO = TIERS.pro;

/**
 * Library bundle prices, as displayed. MIRRORS src/data/library/catalog.json
 * (bundles[].price_cents, loaded into library_bundles), which is what checkout
 * charges. The two Playbook prices are the Stripe Payment Links on /playbook.
 */
export const LIBRARY_PRICES = {
  itemFrom: "$9",
  close_prompt_playbook: "$39",
  close_playbook_skills: "$99",
  pro_month_end_close_kit: "$149",
  smb_year_end_kit: "$79",
  smb_cleanup_kit: "$79",
  smb_monthly_books_kit: "$69",
} as const;

/**
 * One refund promise for every one-time digital product (Library items and
 * bundles, the Playbook, the Search Sprint, the /lists CSVs), worded the same
 * on every page, in the FAQ and in src/content/legal/terms-of-service.md
 * section 6.7 (PRICE-14). The /playbook markup and the purchase emails
 * (supabase/functions/_shared/library.ts, playbook-purchase.ts) carry the
 * same words; keep them agreed.
 *   REFUND_LINE    the short label: "30-day refund"
 *   REFUND_SHORT   next to a buy button: "30-day refund: reply to your receipt"
 *   REFUND_TERMS   the full sentence, for FAQs and fine print
 */
export const REFUND_LINE = "30-day refund";
export const REFUND_SHORT = `${REFUND_LINE}: reply to your receipt`;
export const REFUND_TERMS = "Reply to your receipt within 30 days and we refund the full amount.";

export const OFFERS: Record<OfferKey, Offer> = {
  still_remote: {
    key: "still_remote",
    kicker: `Still Remote pass, ${SPRINT.directoryPriceDisplay} for ${SPRINT.directoryDays} days`,
    headline: "Still Remote: employers that stay remote",
    body: `Companies whose own job boards are at least 60% remote, checked daily. ${SPRINT.directoryPriceDisplay} unlocks the full list and CSV for ${SPRINT.directoryDays} days, nothing renews.`,
    cta: "Browse Still Remote",
    url: "https://stillremote.com/",
    price: SPRINT.directoryPriceDisplay,
    paid: true,
    segments: ["remote"],
    enabled: true,
  },
  still_remote_friday: {
    key: "still_remote_friday",
    kicker: "Free newsletter",
    headline: "Still Remote on Fridays",
    body: "The remote-first employers hiring this week, in one free Friday email.",
    cta: "Get the Friday email",
    url: "https://stillremote.com/",
    price: null,
    paid: false,
    segments: ["remote"],
    enabled: true,
  },
  remote_accountant: {
    key: "remote_accountant",
    kicker: "Free newsletter",
    headline: "The Remote Accountant",
    body: "Remote accounting roles, picked from the board and sent to your inbox. Free, one-click unsubscribe.",
    cta: "Read The Remote Accountant",
    url: "https://remoteaccountingjobs.co/",
    price: null,
    paid: false,
    segments: ["remote", "accounting"],
    enabled: true,
  },
  finance_nomad: {
    key: "finance_nomad",
    kicker: "Free newsletter",
    headline: "The Finance Nomad",
    body: "Remote FP&A, treasury and finance roles, picked from the board and sent to your inbox. Free.",
    cta: "Read The Finance Nomad",
    url: "https://the-finance-nomad.beehiiv.com/",
    price: null,
    paid: false,
    segments: ["remote", "finance"],
    enabled: true,
  },
  candidate_pro: {
    key: "candidate_pro",
    kicker: `Candidate Pro, ${PRO.priceDisplay}${PRO.cadence.replace("/", " a ")}`,
    headline: "Tailor every application",
    body: `Unlimited credits for resume tailoring and interview prep, plus ${PRO.hiredContacts} recruiter contacts a month. Cancel any time.`,
    cta: "See plans",
    url: "/pricing",
    price: PRO.priceDisplay,
    paid: true,
    segments: ["accounting", "finance", "all"],
    enabled: true,
  },
  search_sprint: {
    key: "search_sprint",
    kicker: `Search Sprint, ${SPRINT.priceDisplay} once`,
    headline: "Built for the next 30 days of a job search",
    body: `${SPRINT.days} days of Pro tools, ${SPRINT.hiredContacts} recruiter contacts and the companies trying to hire your role in your city. One payment, nothing renews. Free if you were laid off in the last ${SPRINT.layoffWindowDays} days.`,
    cta: "See the Sprint",
    url: "/sprint",
    price: SPRINT.priceDisplay,
    paid: true,
    segments: ["accounting", "finance", "all"],
    enabled: true,
  },
  hired: {
    key: "hired",
    kicker: "Hired by Audit Friendly",
    headline: "Want a recruiter doing the work?",
    body: "Hired connects you with vetted accounting and finance recruiters who specialize in your niche.",
    cta: "Explore Hired",
    url: "https://hired.auditfriendly.co/",
    price: null,
    paid: false,
    segments: ["accounting", "finance", "all"],
    enabled: true,
  },
  recruiter_list: {
    key: "recruiter_list",
    kicker: "Recruiter list, $19",
    headline: "The 50 recruiters placing US accounting talent",
    body: "Names, firms and contact details in one CSV. One-time payment.",
    cta: "See the list",
    url: "/lists/us-accounting-recruiters",
    price: "$19",
    paid: true,
    segments: ["accounting"],
    enabled: false,
  },
  remote_companies_list: {
    key: "remote_companies_list",
    kicker: "CSV, $19",
    headline: "Remote-friendly companies, with careers links",
    body: "Remote-first and remote-friendly employers with a direct link to each live careers page.",
    cta: "See the list",
    url: "/lists/remote-friendly-companies",
    price: "$19",
    paid: true,
    segments: ["remote"],
    // Off (PRICE-15, 2026-10-20): the $24 Still Remote pass already includes
    // the remote-first company list and CSV, so a $19 CSV offered beside it
    // undercuts the pass. The /lists page stays up until Amir decides whether
    // to retire it or fold it into the pass.
    enabled: false,
  },
  jobs_api: {
    key: "jobs_api",
    kicker: "Jobs API, from $149/mo",
    headline: "Build on the live accounting job feed",
    body: "Every posting and market aggregate, key-gated JSON.",
    cta: "Read the docs",
    url: "/data/api",
    price: "$149",
    paid: true,
    segments: ["employer"],
    enabled: true,
  },
  post_a_job: {
    key: "post_a_job",
    kicker: "Hiring?",
    headline: "Post a job to accounting and finance talent",
    body: "Reach the people reading this board. From $49.",
    cta: "Post a job",
    url: "/post-a-job",
    price: "$49",
    paid: true,
    segments: ["employer"],
    enabled: true,
  },
  library: {
    key: "library",
    kicker: `AuditFriendly Library, from ${LIBRARY_PRICES.itemFrom}`,
    headline: "AI prompts for accounting, each with a known answer",
    body: `Prompts, Claude skills and workflows for ChatGPT, Claude and Copilot. Each ships with sample data and the exact result it must produce. ${REFUND_LINE}.`,
    cta: "Browse the Library",
    url: "/library",
    price: LIBRARY_PRICES.itemFrom,
    paid: true,
    segments: ["accounting", "finance", "smb"],
    enabled: true,
    line: "AI prompts for the close, reconciliations and FP&A, each tested against a known answer.",
  },
  close_prompt_playbook: {
    key: "close_prompt_playbook",
    kicker: `Close Prompt Playbook, ${LIBRARY_PRICES.close_prompt_playbook} once`,
    headline: "Let AI draft your close. You review it.",
    body: `20 month-end prompts for ChatGPT, Claude and Copilot: recs, accruals, flux and more, each with a reviewer's guide. ${REFUND_LINE}.`,
    cta: "See the Playbook",
    url: "/playbook",
    price: LIBRARY_PRICES.close_prompt_playbook,
    paid: true,
    segments: ["accounting"],
    enabled: true,
    line: `20 prompts that draft the recs, accruals and flux for you to review. ${LIBRARY_PRICES.close_prompt_playbook} once.`,
  },
  // The list price. While the founding offer is live, <OfferSlot/> and <OfferLine/> swap in $79 and the founding
  // line (offerWithFounding, src/lib/foundingPrice.ts) from the live status; nothing here turns it on.
  close_playbook_skills: {
    key: "close_playbook_skills",
    kicker: `Playbook + Close Skills, ${LIBRARY_PRICES.close_playbook_skills}`,
    headline: "The Playbook plus five Claude skills",
    body: `The 20 close prompts plus five Claude skills that do the matching and the math: bank rec, flux review, duplicate payments, deferred revenue and journal entry review. ${REFUND_LINE}.`,
    cta: "See the bundle",
    url: "/playbook",
    price: LIBRARY_PRICES.close_playbook_skills,
    paid: true,
    segments: ["accounting"],
    enabled: true,
  },
  pro_month_end_close_kit: {
    key: "pro_month_end_close_kit",
    kicker: `Month-end close kit, ${LIBRARY_PRICES.pro_month_end_close_kit}`,
    headline: "The whole close, with a known answer",
    body: `The AI-assisted month-end close workflow with the Playbook and every Close Skill. ${REFUND_LINE}.`,
    cta: "See the kit",
    url: "/ai-month-end-close",
    price: LIBRARY_PRICES.pro_month_end_close_kit,
    paid: true,
    segments: ["accounting"],
    enabled: true,
  },
  smb_year_end_kit: {
    key: "smb_year_end_kit",
    kicker: `Year-end kit, ${LIBRARY_PRICES.smb_year_end_kit}`,
    headline: "Get your books ready for your CPA",
    body: `Year-end ready for your CPA, the books health check and the 1099 contractor list. Plain English, not tax advice. ${REFUND_LINE}.`,
    cta: "See the kit",
    url: "/year-end-books-for-your-cpa",
    price: LIBRARY_PRICES.smb_year_end_kit,
    paid: true,
    segments: ["smb"],
    enabled: true,
  },
  smb_cleanup_kit: {
    key: "smb_cleanup_kit",
    kicker: `Cleanup kit, ${LIBRARY_PRICES.smb_cleanup_kit}`,
    headline: "Catch up books that fell behind",
    body: `The first-time cleanup workflow with the cleanup prompts and the books health check. ${REFUND_LINE}.`,
    cta: "See the kit",
    url: "/small-business",
    price: LIBRARY_PRICES.smb_cleanup_kit,
    paid: true,
    segments: ["smb"],
    enabled: true,
  },
  smb_monthly_books_kit: {
    key: "smb_monthly_books_kit",
    kicker: `Monthly books kit, ${LIBRARY_PRICES.smb_monthly_books_kit}`,
    headline: "Monthly books in an hour",
    body: `The monthly workflow, the bookkeeping assistant and the monthly prompts, to keep the books current. ${REFUND_LINE}.`,
    cta: "See the kit",
    url: "/small-business",
    price: LIBRARY_PRICES.smb_monthly_books_kit,
    paid: true,
    segments: ["smb"],
    enabled: true,
  },
};

/** The offer id a Library bundle slug sells under (pro-month-end-close-kit to pro_month_end_close_kit). */
export const bundleOfferKey = (slug: string) => slug.replace(/-/g, "_");

/**
 * The URL to link. External links get utm_source=jobs_board, utm_medium=<surface>,
 * utm_campaign=<key>. Internal paths are left clean (a UTM on an internal link
 * starts a new analytics session); offer_click in site_events carries the
 * attribution for those.
 */
export function offerHref(key: OfferKey, surface: OfferSurface): string {
  const o = OFFERS[key];
  if (o.url.startsWith("/")) return o.url;
  const u = new URL(o.url);
  u.searchParams.set("utm_source", "jobs_board");
  u.searchParams.set("utm_medium", surface);
  u.searchParams.set("utm_campaign", key);
  return u.toString();
}

export const isExternalOffer = (key: OfferKey) => !OFFERS[key].url.startsWith("/");

/** Days since the epoch in UTC: the rotation clock, so a page is stable for a day. */
export const dayNumber = (d = new Date()) => Math.floor(d.getTime() / 86_400_000);

/** Pick one enabled offer from a list, rotating by day (plus an optional seed). */
export function rotateOffer(keys: OfferKey[], seed = 0): OfferKey | null {
  const live = keys.filter((k) => OFFERS[k]?.enabled);
  if (!live.length) return null;
  return live[(dayNumber() + seed) % live.length];
}

const FINANCE_RE = /\b(fp\s?&\s?a|fpa|financial (planning|analyst|analysis)|finance|treasury|treasurer|cfo|chief financial|strategic finance|corporate development)\b/i;

/** The matching remote newsletter for a title: finance titles to The Finance Nomad. */
export function remoteNewsletterFor(title?: string | null): OfferKey {
  return FINANCE_RE.test(title ?? "") ? "finance_nomad" : "remote_accountant";
}

/**
 * Which offers fit a job title.
 *   close    people who run or feed the month-end close (accountants,
 *            controllers, GL, AP/AR, bookkeepers): the Playbook fits their
 *            next close as well as their job search
 *   finance  FP&A, treasury and finance titles
 *   other    tax, audit and anything unclear: job-search offers only
 * Tax and audit go first: a "Tax Accountant" does not run a close.
 * MIRRORED in supabase/functions/send-job-alerts/index.ts (roleFamilyFor).
 */
export type RoleFamily = "close" | "finance" | "other";
const NOT_CLOSE_RE = /\b(tax|audit|auditor|assurance|attest)\b/i;
const CLOSE_RE = /\b(accountant|accounting|controller|comptroller|general ledger|gl|bookkeep(er|ing)|reconcil\w*|month[- ]end|close|accounts payable|accounts receivable|ap|ar|ap\/ar|payroll)\b/i;

export function roleFamilyFor(title?: string | null): RoleFamily {
  const t = title ?? "";
  if (NOT_CLOSE_RE.test(t)) return "other";
  if (CLOSE_RE.test(t)) return "close";
  if (FINANCE_RE.test(t)) return "finance";
  return "other";
}

/** Session-level record of the last offer clicked, so checkout_start on the
 *  destination page can say which placement sent the buyer. */
const LAST_OFFER = "af_last_offer";
export function rememberOfferClick(key: string, surface: string, variant?: string) {
  try { window.sessionStorage.setItem(LAST_OFFER, JSON.stringify({ key, surface, variant })); } catch { /* storage blocked */ }
}
/** from_variant is the split-test arm of that placement (apply_offer_v3:search_sprint, job_rail_offer_v3:still_remote, ...),
 *  carried so checkout_start and the purchase can be credited to the arm (offer_funnel in migration 20261020100200). */
export function lastOfferClick(): { from_key?: string; from_surface?: string; from_variant?: string } {
  try {
    const v = JSON.parse(window.sessionStorage.getItem(LAST_OFFER) ?? "null") as { key?: string; surface?: string; variant?: string } | null;
    if (!v) return {};
    const out: { from_key?: string; from_surface?: string; from_variant?: string } = { from_key: v.key, from_surface: v.surface };
    if (v.variant) out.from_variant = v.variant;
    return out;
  } catch { return {}; }
}
